<script setup lang="ts">
import { ref } from "vue";

const { t } = useI18n();

defineProps<{
  donationType: "one-time" | "subscription";
  subscriptionPeriod: "weekly" | "monthly" | "yearly";
  subscriptionPeriods: Array<{ value: string; label: string }>;
  isSubscription: boolean;
  primaryColor?: string | null;
}>();

const emit = defineEmits<{
  "update:donationType": [value: "one-time" | "subscription"];
  "update:subscriptionPeriod": [value: "weekly" | "monthly" | "yearly"];
}>();

// Animation des cœurs qui s'envolent
const floatingHearts = ref<Array<{ id: number; offsetX: number; delay: number }>>([]);
let heartIdCounter = 0;

const handleSubscriptionClick = () => {
  // Générer 5 cœurs avec des positions et délais échelonnés
  const newHearts = Array.from({ length: 5 }, (_, i) => ({
    id: heartIdCounter++,
    offsetX: (Math.random() - 0.5) * 24,
    delay: i * 0.08 + Math.random() * 0.05,
  }));

  floatingHearts.value.push(...newHearts);

  // Retirer les cœurs après l'animation
  setTimeout(() => {
    const ids = newHearts.map(h => h.id);
    floatingHearts.value = floatingHearts.value.filter(h => !ids.includes(h.id));
  }, 1800);

  emit("update:donationType", "subscription");
};
</script>

<template>
  <!-- Switcher type de don -->
  <div class="mb-2">
    <div class="relative bg-gray-100 rounded-xl p-1">
      <!-- Indicateur animé -->
      <div
        class="absolute top-1 bottom-1 w-[calc(50%-4px)] bg-white rounded-lg shadow-sm transition-all duration-300 ease-out"
        :class="donationType === 'one-time' ? 'left-1' : 'left-[calc(50%+2px)]'"
      />

      <!-- Boutons -->
      <div class="relative grid grid-cols-2 gap-1">
        <button
          type="button"
          class="relative z-10 py-2.5 px-4 rounded-lg text-sm font-medium transition-colors duration-200"
          :class="
            donationType === 'one-time'
              ? 'text-gray-900'
              : 'text-gray-500 hover:text-gray-700'
          "
          @click="emit('update:donationType', 'one-time')"
        >
          {{ t('don.typeDon.donUnique') }}
        </button>

        <button
          type="button"
          class="relative z-10 py-2.5 px-4 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center justify-center gap-1.5 overflow-visible"
          :class="
            donationType === 'subscription'
              ? 'text-gray-900'
              : 'text-gray-500 hover:text-gray-700'
          "
          @click="handleSubscriptionClick"
        >
          <!-- Icône avec cœurs qui s'envolent -->
          <span class="relative inline-flex items-center">
            <UIcon
              v-for="heart in floatingHearts"
              :key="heart.id"
              name="i-lucide-heart"
              class="floating-heart w-3 h-3 text-rose-500"
              :style="{
                left: `calc(50% + ${heart.offsetX}px)`,
                animationDelay: `${heart.delay}s`,
              }"
            />
            <UIcon
              name="i-lucide-heart-handshake"
              class="w-4 h-4 text-rose-500"
            />
          </span>
          <span>{{ t('don.typeDon.recurrent') }}</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Période d'abonnement (visible seulement si abonnement sélectionné) -->
  <div
    class="grid transition-all duration-300 ease-in-out"
    :class="isSubscription ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'"
  >
    <div class="overflow-hidden">
      <div class="grid grid-cols-3 gap-2 pb-2">
        <button
          v-for="period in subscriptionPeriods"
          :key="period.value"
          type="button"
          :class="[
            'py-2 px-3 rounded-lg border transition-all duration-200 text-xs font-medium',
            subscriptionPeriod === period.value
              ? (primaryColor ? '' : 'border-blue-500 bg-blue-50 text-blue-600')
              : 'border-gray-200 text-gray-600 hover:border-gray-300',
          ]"
          :style="subscriptionPeriod === period.value && primaryColor ? {
            borderColor: primaryColor,
            backgroundColor: primaryColor + '15',
            color: primaryColor,
          } : undefined"
          @click="
            emit(
              'update:subscriptionPeriod',
              period.value as 'weekly' | 'monthly' | 'yearly'
            )
          "
        >
          {{ period.label }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.floating-heart {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  pointer-events: none;
  z-index: 10;
  animation: floatUp 1.2s ease-out forwards;
}

@keyframes floatUp {
  0% {
    transform: translateX(-50%) translateY(0) scale(1);
    opacity: 1;
  }
  70% {
    opacity: 1;
  }
  100% {
    transform: translateX(-50%) translateY(-50px) scale(0.5);
    opacity: 0;
  }
}
</style>
